<template>
  <print v-if="isPrint" :form="form" :board="board" />
  <div v-else>
    <div class="mt-8">
      <h5><i class="dot"></i> 점검연도 <number-input v-model="form.점검연도" :useComma="false" :align="'left'" style="width: 114px; display: inline-block" class="mx-2" /><span>년</span></h5>

      <h5 class="mt-28px"><i class="dot"></i> 점검구분</h5>
      <div class="mt-4">
        <div class="flex items-center gap-4">
          <span style="min-width: 70px; font-weight: 500; font-size: 14px;">정기점검 :</span>
          <vs-checkbox :value="form.정기점검 === '상반기'" @input="form.정기점검 = $event ? '상반기' : ''" :disabled="board.readonly">상반기</vs-checkbox>
          <vs-checkbox :value="form.정기점검 === '하반기'" @input="form.정기점검 = $event ? '하반기' : ''" :disabled="board.readonly">하반기</vs-checkbox>
        </div>
        <div class="flex items-center gap-4 mt-2">
          <span style="min-width: 70px; font-weight: 500; font-size: 14px;">수시점검 :</span>
          <vs-checkbox :value="form.수시점검 === '해빙기'" @input="form.수시점검 = $event ? '해빙기' : ''" :disabled="board.readonly">해빙기</vs-checkbox>
          <vs-checkbox :value="form.수시점검 === '우수기'" @input="form.수시점검 = $event ? '우수기' : ''" :disabled="board.readonly">우수기</vs-checkbox>
          <vs-checkbox :value="form.수시점검 === '동절기'" @input="form.수시점검 = $event ? '동절기' : ''" :disabled="board.readonly">동절기</vs-checkbox>
          <vs-checkbox :value="form.수시점검 === '기타'" @input="form.수시점검 = $event ? '기타' : ''" :disabled="board.readonly">기타</vs-checkbox>
        </div>
      </div>
    </div>

    <!-- 작성자 정보 -->
    <field-select-module
      v-model="form.stdInfo"
      :visible-field-form="false"
      :use-field-info="false"
      :board="this.board"
      :enabled="true"
      :is-print="isPrint"
      class="mt-28px"
    />

    <!-- 시설 정보 -->
    <div class="mt-28px">
      <h5 class="mb-5"><i class="dot"></i> 시설 정보</h5>
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>시설명</div>
          <field-selector v-model="form.fields" multiple select-all-active />
        </div>
      </div>
    </div>

    <!-- 시설개요 -->
    <div class="mt-28px">
      <h5 class="mb-5"><i class="dot"></i> 시설개요</h5>
      <div class="table-style">
        <table>
          <colgroup>
            <col width="80" />
          </colgroup>
          <thead style="position: sticky; top: 0; z-index: 10; box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1)">
            <tr>
              <th>연번</th>
              <th>소관부서</th>
              <th>담당자</th>
              <th>구분</th>
              <th>시설구분</th>
              <th>시설명</th>
              <th>시설위치<br />(사업장주소)</th>
              <th>관리방법</th>
              <th>면적(㎡), 형식, 연장, 차로수, 높이 등</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(field, index) in form.procFields" :key="`tb-${index}`">
              <td>{{ index + 1 }}</td>
              <td>{{ field.소관부서 }}</td>
              <td>{{ field.담당자 }}</td>
              <td>{{ field.구분 }}</td>
              <td>{{ field.시설구분 }}</td>
              <td>{{ field.시설명 }}</td>
              <td>{{ field.시설위치 }}</td>
              <td>{{ field.관리방법 }}</td>
              <td>{{ field.면적형식연장등 }}</td>
              <td>{{ field.비고 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 점검정보 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 점검정보</h5>
      <div class="writeGridContainer">
        <div>
          <div>점검일</div>
          <date-selector :readonly="board.readonly" v-model="form.점검일" />
        </div>
      </div>
    </div>

    <!-- 점검분야 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 점검분야</h5>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-4 mt-4">
        <div v-for="(_title, _title_i) in getTitles" :key="`checkbox_${_title}`" class="col-span-1 flex items-center">
          <div v-if="isPrint || board.readonly" class="flex items-center">
            <div v-if="form.inspectChecked.includes(_title)" style="background: #200c63" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
              <vs-icon icon="check" size="x-small"></vs-icon>
            </div>
            <div v-else style="border: 2px solid rgb(180, 180, 180)" class="w-5 h-5 rounded-sm mr-2"></div>
            <span class="flex-1">
              {{ _title }}
            </span>
          </div>
          <div v-else>
            <span class="flex">
              <vs-checkbox color="secondary" :disabled="_title_i === 0" v-model="form.inspectChecked" :vs-value="_title">{{ _title }}</vs-checkbox>
            </span>
          </div>
        </div>
      </div>
    </div>

    <TableInspectMerge
      v-for="item in form.inspectList"
      :key="`inspect_${item.title}`"
      v-show="form.inspectChecked.includes(item.title)"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      :title="item.title"
      :tableHeaderTitle="item.tableHeaderTitle || '평가항목'"
      :tableHeaderCheck="item.tableHeaderCheck"
      :tableHeaderNote="item.tableHeaderNote"
      :footer="item.footer"
      v-model="item.list"
      class="mt-5"
    >
      <template #customContent="{ trData, id, input }">
        <div v-if="trData && trData.contentType === 1">
          <p>· 질병이 있는 종업원에 의한</p>
          <p>　 - 영업자 및 집단급식소 설치･운영자의 건강진단 실시(위탁급식소의 경우 제외)</p>
          <p class="flex" style="align-items: center">
            　 - 종업원의 건강진단 실시여부 <ml-input :readonly="board.readonly" class="px-2" v-model="trData.input1" placeholder="입력" style="width: 60px" /> /
            <ml-input :readonly="board.readonly" class="px-2" v-model="trData.input2" placeholder="입력" style="width: 60px" /> 명
          </p>
          <p class="flex" style="align-items: center">
            　 - 건강진단을 받지 아니한 자를 영업에 종사시키는 지 여부 <ml-input :readonly="board.readonly" class="px-2" v-model="trData.input3" placeholder="입력" style="width: 60px" /> /
            <ml-input :readonly="board.readonly" class="px-2" v-model="trData.input4" placeholder="입력" style="width: 60px" /> 명
          </p>
          <p>　 - 건강진단결과 타인에게 위해를 끼칠 우려가 있는 질병이 있다고 인정된 자를 영업에 종사시키는지 여부</p>
        </div>
      </template>
    </TableInspectMerge>

    <!-- 확인자 -->
    <div class="mt-8">
      <div>
        <h5><i class="dot"></i> 결재란</h5>
      </div>
      <name-with-signature-row label-name="작성자" :name.sync="form.sign1.name" :signature.sync="form.sign1.sign" />
      <name-with-signature-row label-name="검토자" :name.sync="form.sign2.name" :signature.sync="form.sign2.sign" />
      <name-with-signature-row label-name="회람자" :name.sync="form.sign3.name" :signature.sync="form.sign3.sign" />
      <name-with-signature-row label-name="승인자" :name.sync="form.sign4.name" :signature.sync="form.sign4.sign" />
    </div>

    <!-- 사진대지 -->
    <photo-upload-grid-module :row-count="2" :grid-count="2" v-model="form.photoList" :is-print="isPrint" :readonly="board.readonly" section-title="사진대지" placeholder="점검 현장 사진" />

    <!--    파일첨부-->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import FileUploadModule from "@/views/modules/FileUploadModule";
import TableInspectMerge from "@/components/TableInspectMerge";
import inspectData from "./inspectData";
import { createSectionedInspectListMigrator } from "@/util/InspectListMigration";

const inspectListMigrator = createSectionedInspectListMigrator(inspectData);
import SignField from "@/components/SignField.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector";
import Print from "./Print";
import NameWithSignatureRow from "@/components/NameWithSignatureRow.vue";
import NumberInput from "@/components/NumberInput.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";

export default {
  components: {
    NameWithSignatureRow,
    Print,
    DateSelector,
    SignField,
    FileUploadModule,
    TableInspectMerge,
    FieldSelectModule,
    PhotoUploadGridModule,
    DisasterClassSelector,
    NumberInput,
    FieldSelector,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      form: {
        점검연도: new Date().getFullYear(),
        정기점검: "",
        수시점검: "",

        stdInfo: {},
        photoList: [],
        fileList: [],

        fields: [],
        procFields: [],

        점검일: "",

        inspectionTitle: "",
        inspectChecked: ["공통분야"],
        inspectList: inspectData,

        sign1: { name: "", sign: "" },
        sign2: { name: "", sign: "" },
        sign3: { name: "", sign: "" },
        sign4: { name: "", sign: "" },
      },
      공중이용시설Codes: {
        도서관: "library",
        "박물관 및 미술관": "museum",
        의료기관: "medical",
        어린이집: "daycare",
        업무시설: "office",
        실내공연장: "indoor-auditorium",
        둘이상용도건축물: "mixed-use-building",
        건축물: "building",
        "항만(방파제)": "port",
        "항만(부두)": "port-dock",
        교량: "bridge",
        터널: "tunnel",
        옹벽: "retaining-wall",
        절토사면: "cutting-slope",
        제방: "dike",
        소규모수도: "waterworks",
        청사: "government-building",
        복합시설: "complex-facility",
        하수도: "sewage-treatment",
        상수도: "water-supply",
        실내체육시설: "indoor-sports-facility",
      },
      원료제조물Codes: {
        급식소: "mass_feeding_facility",
        먹는물: "drinking-water",
        승강기: "lift",
        식품제조가공업: "food-processing",
        "어린이 놀이시설": "kids-play-area",
      },
    };
  },
  computed: {
    getTitles() {
      return this.form.inspectList.map((item) => item.title);
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    projectMode() {
      return parseInt(this.projectId) === 5;
    },
    codes() {
      return { ...this.공중이용시설Codes, ...this.원료제조물Codes };
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            if (key === "정기점검" || key === "수시점검") {
              const v = value[key];
              this.form[key] = Array.isArray(v) ? (v[0] || "") : (v || "");
            } else if (key === "inspectList") {
              this.form.inspectList = inspectListMigrator.needsMigration(value.inspectList)
                ? inspectListMigrator.migrate(value.inspectList)
                : value.inspectList;
            } else {
              this.form[key] = value[key];
            }
          }
        }
        // 기존 stdInfo.field_id에서 fields로 마이그레이션
        if ((!value.fields || value.fields.length === 0) && value.stdInfo && value.stdInfo.field_id) {
          this.$store.dispatch("project/LOAD_FIELD", { postId: value.stdInfo.field_id }).then((field) => {
            if (field) this.form.fields = [field];
          });
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.form.inspectionTitle = value.inspectChecked.join(", ");
        this.$emit("input", value);
      },
    },
    "form.fields": {
      immediate: true,
      deep: true,
      handler(fields) {
        this.form.procFields = this.buildProcFields(fields);
      },
    },
  },
  methods: {
    buildProcFields(fields) {
      const list = Array.isArray(fields) ? fields : [];
      return list.map((item) => {
        const meta = this.parseMeta(item.meta);
        const 종별 = (meta.facility_category && meta.facility_category.classification) || "기타";
        return {
          소관부서: meta.소관부서 || "-",
          담당자: meta.담당주무관 || "-",
          구분: this.get시설분류(meta) || "-",
          시설구분: this.get시설구분(meta, 종별) || "-",
          시설명: item.field_name || "-",
          시설위치: item.address || "-",
          관리방법: meta.관리방법 || "-",
          면적형식연장등: this.get면적형식연장등(meta) || "-",
          비고: meta.remarks || "-",
        };
      });
    },
    parseMeta(meta) {
      if (!meta) return {};
      if (typeof meta === "object") return meta;
      try {
        return JSON.parse(meta);
      } catch (e) {
        return {};
      }
    },
    getFieldTypeToString(type) {
      const keys = Object.keys(this.codes);
      for (const key of keys) {
        if (this.codes[key] === type) return key;
      }
    },
    get시설분류(data) {
      const 공중이용시설Values = Object.values(this.공중이용시설Codes);
      const 원료제조물Values = Object.values(this.원료제조물Codes);
      if (공중이용시설Values.includes(data.field_type)) return "공중이용시설";
      if (원료제조물Values.includes(data.field_type)) return "원료·제조물";
      return "기타";
    },
    get시설구분(data, 종별) {
      const fieldTypeName = this.getFieldTypeToString(data.field_type);
      switch (fieldTypeName) {
        case "건축물": return "32-건축물";
        case "교량":
          if (data && data.facility_category) {
            const classification = data.facility_category.classification || "";
            if (data.facility_category.cat2.includes("도로교량")) {
              if (classification === "1종") return "25-1종 교량";
              if (classification === "2종") return "25-2종 교량";
              if (classification === "3종") return "98-3종 교량";
            } else if (data.facility_category.cat2.includes("철도")) {
              if (classification === "1종") return "26-1종 교량";
              if (classification === "2종") return "26-2종 교량";
              if (classification === "3종") return "100-3종 교량";
            }
          }
          return "교량";
        case "도서관": return "7-도서관";
        case "둘이상용도건축물": return "17-둘이상용도건축물";
        case "박물관 및 미술관": return "8-박물관 및 미술관";
        case "복합시설": return "17-복합시설";
        case "상수도": return "36-상수도";
        case "수도":
        case "소규모수도": return "89-수도법";
        case "실내체육시설": return "19-실내 체육시설";
        case "실내공연장": return "18-실내 공연장";
        case "어린이집": return "11-어린이 집";
        case "업무시설": return "16-업무시설";
        case "옹벽":
          if (종별 === "1종") return "38-1종 옹벽";
          if (종별 === "2종") return "38-2종 옹벽";
          if (종별 === "3종") return "38-3종 옹벽";
          return "38-옹벽";
        case "의료기관": return "9-의료기관";
        case "절토사면":
          if (종별 === "1종") return "38-1종 절토사면";
          if (종별 === "2종") return "38-2종 절토사면";
          if (종별 === "3종") return "38-3종 절토사면";
          return "38-절토사면";
        case "제방": return "34-제방";
        case "청사": return "16-청사";
        case "터널":
          if (data && data.facility_category) {
            const classification = data.facility_category.classification || "";
            if (data.facility_category.cat2.includes("도로")) {
              if (classification === "1종") return "27-1종 터널";
              if (classification === "2종") return "27-2종 터널";
              if (classification === "3종") return "99-3종 터널";
            } else if (data.facility_category.cat2.includes("철도")) {
              if (classification === "1종") return "28-1종 터널";
              if (classification === "2종") return "28-2종 터널";
              if (classification === "3종") return "101-3종 터널";
            }
          }
          return "터널";
        case "하수도": return "37-하수도";
        case "항만(방파제)": return "29-방파제";
        case "항만(부두)": return "30-부두";
        case "급식소": return "69-식품, 식품첨가물, 기구 및 용기포장(집단급식소)";
        case "먹는물": return "90-먹는물 관리법";
        case "승강기": return "85-승강기 안전관리법";
        case "식품제조가공업": return "69-식품제조가공업";
        case "어린이 놀이시설": return "84-어린이 놀이시설 안전관리법";
        default: return `${fieldTypeName} - ${종별}`;
      }
    },
    get면적형식연장등(data) {
      const meta = this.parseMeta(data.meta) || data;
      let result = "";
      if (meta.건축_연면적) result += `건축 연면적: ${meta.건축_연면적}, `;
      if (meta.층수_최고높이) result += `층수-최고높이: ${meta.층수_최고높이}, `;
      if (meta.연장_길이) result += `연장-길이: ${meta.연장_길이}, `;
      if (meta.차로수_상행) result += `차로수-상행: ${meta.차로수_상행}, `;
      if (meta.차로수_하행) result += `차로수-하행: ${meta.차로수_하행}, `;
      if (meta.시설용량) result += `시설용량: ${meta.시설용량}, `;
      if (meta.경간형식) result += `경간형식: ${meta.경간형식}, `;
      if (meta.연장) result += `연장: ${meta.연장}, `;
      if (meta.높이) result += `높이: ${meta.높이}, `;
      if (meta.주요공법) result += `주요공법: ${meta.주요공법}, `;
      if (meta.일평균) result += `일평균: ${meta.일평균}, `;
      if (meta.제방연장) result += `제방연장: ${meta.제방연장}, `;
      if (meta.종류) result += `종류: ${meta.종류}, `;
      if (meta.허용월파량_호안) result += `허용월파량(호안): ${meta.허용월파량_호안}, `;
      if (meta.대표형식) result += `대표형식: ${meta.대표형식}, `;
      if (meta.일평균_식수인원) result += `일평균 식수인원: ${meta.일평균_식수인원}, `;
      if (meta.기타_상세제원 || meta.otherSpecifications) result += `기타 상세제원: ${meta.기타_상세제원 || meta.otherSpecifications}, `;
      result = result.slice(0, -2);
      return result.length === 0 ? "-" : result;
    },
  },
};
</script>

<style scoped lang="scss">
.table-style {
  height: 300px;
  background-color: #fafafa;
  overflow-y: auto;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    th,
    td {
      border: 1px solid #cdcdcd;
      border-left: 0;
      border-right: 0;
    }

    thead {
      box-shadow: none !important;

      th {
        font-weight: 700;
        font-size: 14px;
        text-align: center;
        padding: 16px 0;
        background-color: #fafafa;
      }
    }

    td {
      font-weight: 400;
      font-size: 14px;
      text-align: center;
      padding: 10px 0;
      background-color: white;
    }
  }
}
</style>
